import { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { getBlackoutTargets } from "store/blackouts"
import { BLACKOUT_SCOPES, RESOURCE_FILTER_GROUPS } from "../constants"
import { flattenTargetOptions } from "../utils"

/** The filters offer the same things a blackout can target, so they read the same endpoint. */
const FILTER_SCOPES = [BLACKOUT_SCOPES.LISTING, BLACKOUT_SCOPES.CREW, BLACKOUT_SCOPES.EQUIPMENT]

/** A stable empty fallback: a fresh `{}` from the selector would re-run both memos every render. */
const NO_TARGETS = {}

/**
 * Options for the Resources and Listings filters.
 *
 * `state.blackouts.targets` is shared with the create/edit form, which fetches the one scope it
 * needs when it opens; fetching all three here only warms the same cache.
 */
export const useBlackoutFilterOptions = ({ enabled = true } = {}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const targets = useSelector((state) => state.blackouts?.targets?.data ?? NO_TARGETS)

  useEffect(() => {
    if (!enabled) return
    // `api()` rejects on failure and has already notified by then; an empty filter list is the
    // right fallback, and the page's own list request is what the guide is here for.
    FILTER_SCOPES.forEach((scope) => dispatch(getBlackoutTargets(scope)).catch(() => {}))
  }, [dispatch, enabled])

  const resourceGroups = useMemo(
    () =>
      RESOURCE_FILTER_GROUPS.map((group) => ({
        key: group.key,
        label: t(group.labelKey, { defaultValue: group.labelDefault }),
        iconName: group.iconName,
        options: flattenTargetOptions(targets[group.key])
      })).filter((group) => group.options.length > 0),
    [targets, t]
  )

  const listingGroups = useMemo(
    () => [{ key: BLACKOUT_SCOPES.LISTING, options: flattenTargetOptions(targets[BLACKOUT_SCOPES.LISTING]) }],
    [targets]
  )

  return { resourceGroups, listingGroups }
}

export default useBlackoutFilterOptions
